import { useMemo } from 'react';
import type { Study } from '../../../api/types';
import { useProtocols } from './useProtocols';
import { getBestProtocol } from './matchProtocol';
import { isMultiScreenBritProtocol } from './britToOhif';
import { useMonitorManager } from '../../multi-monitor';

/**
 * Hook that returns the best-matching protocol for a study.
 * Combines protocol loading (from useProtocols) with client-side matching.
 *
 * When multiple monitors are available, multi-screen protocols are preferred
 * over single-screen ones, provided they achieve the same match quality.
 */
export function useBestProtocol(study: Study) {
  const { data: protocols } = useProtocols('all');
  const { capability, childWindows } = useMonitorManager();

  const hasChildWindows = childWindows.length > 0 && capability.available;

  return useMemo(() => {
    if (!protocols) return null;
    const all = Object.values(protocols);

    if (!hasChildWindows) {
      // No secondary monitors — just pick the best score regardless
      return getBestProtocol(study, all);
    }

    // With multiple monitors available, try to prefer multi-screen protocols.
    const multiScreen = all.filter(isMultiScreenBritProtocol);
    const singleScreen = all.filter((p) => !isMultiScreenBritProtocol(p));

    // First try: best multi-screen match
    const bestMulti = getBestProtocol(study, multiScreen);
    // Also compute: best single-screen match (for fallback)
    const bestSingle = getBestProtocol(study, singleScreen);

    // If a multi-screen protocol matched, prefer it
    if (bestMulti) return bestMulti;

    // Otherwise fall back to single-screen (still useful — primary gets
    // the layout, and priors can be sent to other windows)
    return bestSingle;
  }, [study, protocols, hasChildWindows]);
}
